import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useRequireAuth } from '@/lib/auth/useRequireAuth';
import { subscriptionQueries } from '@/lib/queries/subscription';

export const Route = createFileRoute('/subscriber/welcome')({
  component: WelcomeRoute,
});

function WelcomeRoute() {
  const { user, loading } = useRequireAuth();
  const subscription = useQuery({
    ...subscriptionQueries.current(user?.id),
    refetchInterval: (q) => {
      const data = q.state.data as { status?: string } | null | undefined;
      const isActive = data?.status === 'active' || data?.status === 'trialing';
      return isActive ? false : 2000;
    },
  });
  const queryClient = useQueryClient();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (loading) return null;

  const sub = subscription.data;
  const isReady = sub && (sub.status === 'active' || sub.status === 'trialing');

  return (
    <div className="min-h-screen bg-parchment">
      <main className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-6 py-16">
        {!isReady && (
          <div className="text-center">
            <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">
              Setting up your subscription
            </p>
            <h1 className="mt-3 font-serif text-3xl text-ink balance sm:text-4xl">
              Confirming your payment…
            </h1>
            <p className="mt-4 font-sans text-base text-ink/70 pretty">
              This usually takes a few seconds. We&apos;re waiting for Stripe to confirm.
            </p>
            {elapsedSeconds > 30 && (
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ['subscription', 'current', user?.id],
                    });
                  }}
                  className="rounded-md border border-ink/20 px-4 py-2 font-sans text-sm font-medium text-ink hover:border-oxblood hover:text-oxblood"
                >
                  Refresh status
                </button>
                <p className="mt-3 text-xs text-ink/50">
                  If this is still showing in another minute, check your email — Stripe sends a
                  receipt the moment payment clears.
                </p>
              </div>
            )}
          </div>
        )}

        {isReady && (
          <div className="text-center">
            <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Welcome</p>
            <h1 className="mt-3 font-serif text-4xl text-ink balance sm:text-5xl">
              Welcome to ArcheTelos.
            </h1>
            {sub.is_founder && sub.founder_number !== null && (
              <p className="mt-6 inline-flex items-center gap-2 rounded-sm border border-oxblood/40 bg-oxblood/5 px-3 py-1 font-sans text-xs uppercase tracking-[0.18em] text-oxblood">
                Founding Member #{sub.founder_number}
              </p>
            )}
            {sub.is_founder && sub.founder_number !== null && (
              <p className="mt-4 font-sans text-sm text-ink/70 pretty">
                Your place among the five hundred is permanent — recognition on the record to
                follow.
              </p>
            )}

            <p className="mt-8 font-sans text-base text-ink/70 pretty">
              A confirmation has been sent to your email. Three quick places to start:
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <WelcomeLink to="/subscriber" label="Your home" />
              <WelcomeLink to="/subscriber/settings" label="Settings" />
              <WelcomeLink
                href={`${import.meta.env.PUBLIC_MARKETING_URL ?? 'https://archetelos.stream'}/search?type=article`}
                label="Latest essays"
                external
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

interface WelcomeLinkProps {
  to?: string;
  href?: string;
  label: string;
  external?: boolean;
}
function WelcomeLink({ to, href, label, external }: WelcomeLinkProps) {
  const className =
    'rounded-md border border-ink/15 px-4 py-3 font-sans text-sm text-ink hover:border-oxblood hover:text-oxblood';
  if (external && href) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }
  return (
    <Link to={to ?? '/'} className={className}>
      {label}
    </Link>
  );
}
